import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import AdminNavbar from "./components/AdminNavbar";
import { typography, surfaces, borders, textColors } from "../design-system/tokens";
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "../design-system/icons";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SIDEBAR_STORAGE_KEY = "admin-sidebar-open";

const SIDEBAR_NAV_ITEMS = [
  { to: "dashboard", label: "Dashboard" },
  { to: "users", label: "Users" },
  { to: "affiliates", label: "Affiliates" },
  { to: "affiliate-payments", label: "Affiliate Payments" },
  { to: "coupons", label: "Coupons" },
  { to: "global-discount", label: "Global Discount" },
  { to: "subscription-config", label: "Subscriptions" },
  { to: "global-access-grant", label: "Global Access Grant" },
  { to: "website-previews", label: "Website Previews" },
  { to: "feedbacks", label: "Feedbacks" },
  { to: "leads", label: "Leads" },
  { to: "landing-websites", label: "Landing Websites" },
  { to: "broadcasts", label: "Broadcasts" },
  { to: "automations", label: "Automations" },
  { to: "messages", label: "Messages" },
  { to: "account-deletions", label: "Account Deletions" },
  { to: "blog", label: "Blog" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Inline hamburger icon (no dedicated icon in the design-system). */
const MenuIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const getSidebarLinkClasses = ({ isActive }) => {
  const base = `block px-3 py-2 rounded-md transition-colors ${typography.bodySmall} truncate`;
  const active = `${surfaces.active} ${textColors.primary} font-medium`;
  const inactive = `${textColors.secondary} ${surfaces.hover}`;
  return `${base} ${isActive ? active : inactive}`;
};

// ---------------------------------------------------------------------------
// Sidebar nav list (shared between desktop & mobile drawers)
// ---------------------------------------------------------------------------

const SidebarNav = () => (
  <nav className="flex flex-col gap-0.5 px-3 py-3 overflow-y-auto flex-1">
    {SIDEBAR_NAV_ITEMS.map(({ to, label }) => (
      <NavLink
        key={to}
        to={to}
        end={to !== "dashboard"}
        className={getSidebarLinkClasses}
      >
        {label}
      </NavLink>
    ))}
  </nav>
);

// ---------------------------------------------------------------------------
// Dashboard (layout shell)
// ---------------------------------------------------------------------------

const Dashboard = () => {
  const location = useLocation();

  // Desktop sidebar open/close – persisted in localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  // Mobile overlay sidebar
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const toggleDesktopSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota errors */
      }
      return next;
    });
  }, []);

  // Close mobile sidebar whenever the route changes
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className={`min-h-screen flex flex-col ${surfaces.modernApp}`}>
      <AdminNavbar />

      <div className="flex flex-1 overflow-hidden">
        {/* ================================================================
            DESKTOP SIDEBAR (hidden below md)
            ================================================================ */}
        <aside
          className={[
            "hidden md:flex flex-col shrink-0 overflow-hidden",
            surfaces.glass,
            `border-r ${borders.glass}`,
            "transition-[width] duration-300 ease-in-out",
            sidebarOpen ? "w-56" : "w-0 !border-r-transparent",
          ].join(" ")}
        >
          {/* Inner container keeps a fixed width so content doesn't reflow */}
          <div className="w-56 flex flex-col h-full min-h-0">
            {/* Header */}
            <div
              className={`flex items-center justify-between px-4 py-3 border-b ${borders.glass} shrink-0`}
            >
              <span
                className={`${typography.bodySmall} ${textColors.tertiary} font-medium uppercase tracking-wider select-none`}
              >
                Navigation
              </span>
              <button
                type="button"
                onClick={toggleDesktopSidebar}
                className={`p-1 rounded-md transition-colors ${surfaces.hover} ${textColors.secondary}`}
                aria-label="Collapse sidebar"
              >
                <ChevronLeftIcon size="sm" />
              </button>
            </div>

            <SidebarNav />
          </div>
        </aside>

        {/* ================================================================
            MOBILE OVERLAY SIDEBAR (hidden at md+)
            ================================================================ */}
        {/* Backdrop */}
        <div
          className={[
            "fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 md:hidden",
            mobileSidebarOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none",
          ].join(" ")}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer */}
        <aside
          className={[
            "fixed top-0 left-0 bottom-0 z-50 w-64 flex flex-col shadow-xl md:hidden",
            surfaces.glassCard,
            "transition-transform duration-300 ease-in-out",
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full",
          ].join(" ")}
        >
          <div
            className={`flex items-center justify-between px-4 py-3 border-b ${borders.glass} shrink-0`}
          >
            <span
              className={`${typography.bodySmall} ${textColors.tertiary} font-medium uppercase tracking-wider select-none`}
            >
              Navigation
            </span>
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(false)}
              className={`p-1 rounded-md transition-colors ${surfaces.hover} ${textColors.secondary}`}
              aria-label="Close navigation"
            >
              <XIcon size="sm" />
            </button>
          </div>

          <SidebarNav />
        </aside>

        {/* ================================================================
            MAIN CONTENT AREA
            ================================================================ */}
        <div className="flex-1 flex flex-col min-w-0">
          {/*
            Toggle bar
            - Mobile: always visible (hamburger menu)
            - Desktop: only visible when sidebar is collapsed (expand chevron)
          */}
          <div
            className={[
              "flex items-center gap-2 px-4 py-2",
              `border-b ${borders.glass}`,
              sidebarOpen ? "md:hidden" : "",
            ].join(" ")}
          >
            {/* Mobile hamburger (visible < md) */}
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(true)}
              className={`p-1.5 rounded-md transition-colors ${surfaces.hover} ${textColors.secondary} md:hidden`}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </button>

            {/* Desktop expand button (visible >= md, only when sidebar collapsed) */}
            {!sidebarOpen && (
              <button
                type="button"
                onClick={toggleDesktopSidebar}
                className={`hidden md:inline-flex items-center gap-1.5 p-1.5 rounded-md transition-colors ${surfaces.hover} ${textColors.secondary}`}
                aria-label="Expand sidebar"
              >
                <ChevronRightIcon size="sm" />
                <span className={`${typography.bodySmall} ${textColors.tertiary}`}>Menu</span>
              </button>
            )}
          </div>

          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
